// Owner activity-log query. Range filter (Today / 3d / 7d / 30d / 60d / 90d
// / All time) + optional branch filter + activity-type filter (all /
// single stamp / multi stamp / redemption) + free-text search (customer
// name or card code). Cursor-paginated via useInfiniteQuery — "Load more"
// fetches the next page from where the last one left off, rather than
// re-fetching the whole range.

import { useInfiniteQuery } from '@tanstack/react-query'

import { ownerApi } from '../ownerApi.js'

export type HistoryRange = 'today' | '3d' | '7d' | '30d' | '60d' | '90d' | 'all'
export type ActivityTypeFilter =
  | 'all'
  | 'single_stamp'
  | 'multi_stamp'
  | 'redemption'
  | 'adjustment'

interface ActorFields {
  branch_id: string | null
  branch_name: string | null
  staff_user_id: string | null
  staff_display_name: string | null
}

interface CustomerFields {
  customer_id: string
  customer_first_name: string | null
  customer_last_name: string | null
  card_code: string
  /** Customer lifecycle (2026-09-05) — opens the customer detail drawer
   *  (adjust stamps / revoke / erase) straight from a Logs row. */
  card_id: string
}

export interface StampLogItem extends ActorFields, CustomerFields {
  kind: 'stamp'
  id: string
  timestamp: string
  source: string
  /** How many stamps this one action added — >1 means a multi-stamp batch. */
  stamps_added: number
  stamp_from: number
  stamp_to: number
  total_stamps: number
  /** How many of `stamps_added` were tagged off-menu ("secret menu"). */
  secret_stamp_count: number
}

export interface RedemptionLogItem extends ActorFields, CustomerFields {
  kind: 'redemption'
  id: string
  timestamp: string
  reward_text: string
  milestone_type: 'intermediate' | 'final'
}

// Customer lifecycle (2026-09-05) — a manual owner stamp correction.
export interface AdjustmentLogItem extends ActorFields, CustomerFields {
  kind: 'adjustment'
  id: string
  timestamp: string
  delta: number
  secret_delta: number
  previous_count: number
  new_count: number
  reason: string
}

export type ActivityLogItem = StampLogItem | RedemptionLogItem | AdjustmentLogItem

export interface StampHistoryResponse {
  items: ActivityLogItem[]
  next_cursor: string | null
}

export function stampHistoryKey(
  range: HistoryRange,
  branchId: string | undefined,
  type: ActivityTypeFilter,
  q: string,
  customerId?: string,
) {
  return ['owner', 'stamp-history', { range, branchId, type, q, customerId }] as const
}

export function useStampHistory(
  range: HistoryRange,
  branchId?: string,
  type: ActivityTypeFilter = 'all',
  q = '',
  // Customer lifecycle (2026-09-05) — locks the feed to one customer's full
  // history (the customer-detail modal's "Activity" section) instead of
  // free-text search. Mutually exclusive with `q` in practice.
  customerId?: string,
) {
  return useInfiniteQuery({
    queryKey: stampHistoryKey(range, branchId, type, q, customerId),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) => {
      const qs = new URLSearchParams({ range, type })
      if (branchId) qs.set('branch_id', branchId)
      if (q.trim()) qs.set('q', q.trim())
      if (customerId) qs.set('customer_id', customerId)
      if (pageParam) qs.set('cursor', pageParam)
      return ownerApi.get<StampHistoryResponse>(
        `/owner/stamp-history?${qs.toString()}`,
      )
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 30_000,
  })
}
